/* eslint-disable no-shadow */
import _ from 'lodash';
import types from './mutation-types';
import charts from './charts';
import traces from './traces';

export default {
  namespaced: true,
  modules: {
    charts,
    traces,
  },
  state: () => ({
    dataPointsCount: null,
    // Filter states
    filterSelectedArea: '',
    filterSelectedSite: [],
    filterSelectedInfra: [],
    filterSelectedInst: [],
  }),
  mutations: {
    // In InstrumentFilter.vue
    [types.FILTER_AREA_SELECTION]: (state, filterSelectedArea) => {
      state.filterSelectedArea = filterSelectedArea;
    },
    [types.FILTER_INFRA_SELECTION]: (state, filterSelectedInfra) => {
      state.filterSelectedInfra = filterSelectedInfra;
    },
    [types.FILTER_INST_SELECTION]: (state, filterSelectedInst) => {
      state.filterSelectedInst = filterSelectedInst;
    },
    [types.FILTER_SITE_SELECTION]: (state, filterSelectedSite) => {
      state.filterSelectedSite = filterSelectedSite;
    },
    // End InstrumentFilter.vue
    [types.UPDATE_DATA_POINT_COUNT]: (state, dataPointsCount) => {
      state.dataPointsCount = dataPointsCount;
    },
  },
  actions: {
    // Data Visualization Interaction - used in tracetabs.vue
    setDataPointsCount: ({ commit }, { dataPointsCount }) => {
      commit(types.UPDATE_DATA_POINT_COUNT, dataPointsCount);
    },
    // Viz App Filter
    filterAreaSelection: ({ commit }, { selected }) => {
      commit(types.FILTER_AREA_SELECTION, selected);
    },
    // Viz App Filter
    filterSiteSelection: ({ commit }, { selected }) => {
      commit(types.FILTER_SITE_SELECTION, selected);
    },
    // Viz App Filter
    filterInfraSelection: ({ commit }, { selected }) => {
      commit(types.FILTER_INFRA_SELECTION, selected);
    },
    // Viz App Filter
    filterInstSelection: ({ commit }, { selected }) => {
      commit(types.FILTER_INST_SELECTION, selected);
    },
    /**
     * Renders the plot by modifying the appropriate plotly object
     * @param {string} chartUUID - The uuid of the chart to be modified.
     * @param {object} data - The response data from the data service.
     * @param {string} title - The plot title to be assigned.
     * @param {object} parameterMapping - The parameter mapping object.
     * @param {object} requestBody - The request body sent to data service.
     * @param {object} colorscales - The cmocean colorbar scales.
     */
    renderPlot: async ({ dispatch, getters }, {
      chartUUID, data, title,
      parameterMapping, requestBody, colorscales,
    }) => {
      const defaultMarkerColor = 'rgb(57, 149, 163)';
      const chart = getters['charts/find'](chartUUID);
      const layout = { ...chart.chart.layout, title };
      const traces = getters['traces/findByUUID'](chartUUID);

      /** Retrieve Parameters Info */
      const xParam = parameterMapping[requestBody.x];
      const yParam = parameterMapping[requestBody.y];
      const xTitle = `${xParam.parameter_name} (${xParam.unit})`;
      const yTitle = `${yParam.parameter_name} (${yParam.unit})`;

      /** Clears the no data chart annotation */
      layout.annotations[0].visible = false;
      dispatch('charts/updatePlotLayout', { uuid: chartUUID, layout });

      /** Changes the downloaded image file name */
      const options = { ...chart.chart.options };
      const fileName = title.split(/,| /).join('_');
      options.toImageButtonOptions.filename = fileName;
      options.displayModeBar = true;
      dispatch('charts/updatePlotOptions', { uuid: chartUUID, options });

      /** Iterate over each trace */
      traces.forEach((trace, idx) => {
        const result = data.result[idx];
        let newTrace = _.defaultsDeep({}, trace);
        newTrace.marker = {
          color: defaultMarkerColor,
          size: 3,
        };

        /** Set trace data */
        newTrace.x = result.x;
        newTrace.y = result.y;
        // Check if data is shaded
        if (result.shaded) {
          newTrace.z = result.z;
        }

        const roundX = trace.selectedAxis.x === 'time' ? '%{x}' : '%{x:.2f}';

        /** Layout modifications */
        const currentLayoutIdx = idx === 0 ? '' : idx + 1;
        const currentXLayout = `xaxis${currentLayoutIdx}`;
        const currentYLayout = `yaxis${currentLayoutIdx}`;
        newTrace.xaxis = `x${currentLayoutIdx}`;
        newTrace.yaxis = `y${currentLayoutIdx}`;

        // If multiple datasets, can change
        // individual dataset layout here
        // x axis
        layout[currentXLayout] = {
          ...layout.xaxis,
          title: {
            ...layout.xaxis.title, text: xTitle,
          },
          type: requestBody.x === 'time' ? 'date' : 'linear',
          // Let plotly determine the ranges for plot at first
          autorange: true,
          range: null,
          // xaxis and yaxis false so zoom isn't restricted on plot after it renders
          fixedrange: false,
        };
        // end x axis

        // y axis
        layout[currentYLayout] = {
          ...layout.yaxis,
          title: {
            ...layout.yaxis.title, text: yTitle,
          }, // By default reverse y axis if parameter is pressure
          autorange: requestBody.y.includes('pressure') ? 'reversed' : true,
          range: null,
          // xaxis and yaxis false so zoom isn't restricted on plot after it renders
          fixedrange: false,
        };
        // end y axis
        dispatch('charts/updatePlotLayout', { uuid: chartUUID, layout });
        /* End Layout modifications */

        // Check if data is shaded
        if (result.shaded) {
          newTrace.type = 'heatmap';
          newTrace.colorscale = [
            ['0.0', defaultMarkerColor],
            ['1.0', defaultMarkerColor],
          ];
          newTrace.showscale = false;
          newTrace.colorbar = null;
          newTrace.hoverinfo = 'x+z';
          newTrace.hovertemplate = `${requestBody.x}: ${roundX}<br>${requestBody.y}: %{z:.2f}<extra></extra>`;
        }

        // Colorbar data
        const colorAxis = newTrace.selectedAxis.color;
        if (colorAxis.active) {
          const cParam = parameterMapping[requestBody.color];
          const colorTitle = `${cParam.parameter_name} (${cParam.unit})`;
          const colormap = colorscales[colorAxis.colorbar];
          /* Determine color range */
          const flattenColor = _.flattenDeep(result.z);
          let colorMin = colorAxis.range.min;
          let colorMax = colorAxis.range.max;

          if (!colorMin) {
            colorMin = _.min(flattenColor);
          }

          if (!colorMax) {
            colorMax = _.max(flattenColor);
          }

          // Round colorMin and colorMax to 3 decimal points
          colorMin = _.round(colorMin, 3);
          colorMax = _.round(colorMax, 3);

          // Set form min max
          newTrace.selectedAxis.color.range.min = colorMin;
          newTrace.selectedAxis.color.range.max = colorMax;
          /* ======================= */

          if (result.shaded) {
            // Shaded and colorbar is on
            newTrace = _.omit(newTrace, ['marker', 'mode']);
            newTrace = {
              ...newTrace,
              hoverinfo: 'x+y+z',
              hovertemplate: `${requestBody.x}: ${roundX}<br>${requestBody.y}: %{y:.2f}<br>${requestBody.color}: %{z:.2f}<extra></extra>`,
              colorbar: {
                title: `${colorTitle}<br><br>&nbsp;`, // adds space between titie and ticks
                titleside: 'right', // puts the colorbar title on right and vertical
                tickmode: 'auto',
                tickformat: '0.4r',
              },
              colorscale: colormap,
              zauto: false,
              zmin: colorMin,
              zmax: colorMax,
              showscale: true,
            };
          } else {
            // Make marker color from colorbar
            newTrace.marker = {
              ...newTrace.marker,
              colorscale: colormap,
              color: result.z,
              cauto: false,
              cmin: colorMin,
              cmax: colorMax,
              colorbar: {
                title: `${colorTitle}<br><br>&nbsp;`, // adds space between title and ticks
                titleside: 'right', // puts the colorbar title on right and vertical
                tickmode: 'auto',
                tickformat: '0.4r',
              },
              showscale: true,
            };
          }
        }

        dispatch('traces/updateTrace', { id: newTrace.id, updateObj: newTrace });
      });
    },
  },
};
